import { HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";

import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";
import { CheckCircleIcon, ChevronRightIcon, ExclamationTriangleIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";

import BulkListModal from "./BulkListModal";
import PartImage from "../../stock/PartImage";
import PopOutBox from "../../shared/PopOutBox";
import ClickToCopy from "../../shared/ClickToCopy";
import UnlistedNpmUpdate from "./UnlistedMpnUpdate";
import { IPartCategory } from '../../../models/Part';
import { PageMode } from "../../../models/PagedTable";
import UnlistedOfferPrice from "./UnlistedOfferPrice";
import openInNewTab from "../../../utils/openInNewTab";
import PagedTable from "../../shared/Tables/PagedTable";
import UnlistedGradeUpdate from "./UnlistedGradeUpdate";
import UnlistedReadyStatus from "./UnlistedReadyStatus";
import UnlistedKTypeUpdate from "./UnlistedKTypeUpdate";
import UnlistedCategoryRow from "./UnlistedCategoryRow";
import { IPart, IPartImageType } from "../../../models/Part";
import formatDateTime from './../../../utils/formatDateTime';
import usePartMutations from "../../../data/usePartMutations";
import PartStatusLabel from "../../ebayOrders/PartStatusLabel";
import useNotification from "../../notifications/useNotifications";
import UnlistedExpandedRowContent from "./UnlistedExpandedRowContent";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import ChangeCategoryModal from "../../stock/stockFunctions/ChangeCatgeoryModal";
import WarehouseLocationFromId from "../../warehouseLocations/WarehouseLocationFromId";
import WarehouseLocationSearch from "../../warehouseLocations/WarehouseLocationSearch";
import useTemplateProductDescription from "../../../data/useTemplateProductDescription";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import DateRangeFilter from "../../shared/DateRangeFilter";
import Card from "../../shared/Card/Card";
import EbayListingTitle from "../EbayListingTitle";
import Tooltip from "../../shared/Tooltip";
import SelectInput from "../../shared/Inputs/SelectInput";
import PartCategoryCombo from "../../leads/LeadParts/PartCategoryCombo";
import { useForm } from "react-hook-form";
import { FormatType, ListingStatusEnum } from "../../../models/EbayOffer";
import { IWarehouseLocation } from "../../../models/Location";
import { useQueryClient } from "@tanstack/react-query";
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput";
import useWarehouseLocations from "../../../data/useWarehouseLocations";
import RecentlyListedErrorCell from "../recentlyListed/RecentlyListedErrorCell";
import TableCheckboxCol, { IndeterminateCheckbox } from "../../shared/Tables/IndeterminateCheckbox";
import useCategoriesPartial from "../../../data/useCategoriesPartial";

type LocationSearchMode = "equals" | "beginsWith";

interface IFormFields {
  partCategory: IPartCategory | undefined;
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
};

const defaultFormValues: IFormFields = {
  partCategory: undefined,
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created"
};

export default function EbayUnlistedTable() {
  const { autoReorderImages } = usePartMutations();
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategoriesPartial();
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const { register, setValue, getValues, watch, reset, handleSubmit, formState } = useForm<IFormFields>({
    defaultValues: defaultFormValues,
  });

  const [bulkListLoading, setBulkListLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState<boolean>(false);
  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);
  const tableRef = useRef<Table<IPart> | null>(null);
  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const watchPartCategory = watch("partCategory");
  const watchListingStatus = watch("listingStatus");
  const watchWarehouseLocation = watch("warehouseLocation");
  const watchLocationSearchMode = watch("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");

  // const ebayListingOptions = Object.keys(ListingStatusEnum)
  //   .filter(key => isNaN(Number(key)))
  //   .map((key, index) => ({
  //     id: index,
  //     name: key,
  //   }));

  const colHelper = createColumnHelper<IPart>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, true, "ebay-listings"),
      colHelper.display({
        id: "images",
        cell: (info) => <PartImage part={info.row.original} width="min-w-10 max-w-32" />
      }),
      colHelper.accessor("categoryId", {
        header: "Category",
        id: "categoryId",
        sortDescFirst: false,
        sortingFn: "auto",
        cell: (info) => <UnlistedCategoryRow part={info.row.original} categories={allCategories.data?.data} />,
      }),
      colHelper.accessor("tagNumber", {
        header: "Tag No.",
        id: "tagNumber",
        sortingFn: "auto",
        cell: (info) => <div className="flex flex-col">
          <ClickToCopy text={info.row.original.tagNumber} />
        </div>,
      }),
      colHelper.accessor("vehicle.vrm", {
        header: "Vehicle",
        id: "vrm",
        sortingFn: "auto",
        cell: (info) => <div className="flex flex-col">
          {/* <span>{info.row.original.vehicle?.vrm ?? ""}</span> */}
          <ClickToCopy text={info.row.original.vehicle?.vrm} />
          <span>{info.row.original.vehicle?.combinedMake ?? ""}</span>
          <span>{info.row.original.vehicle?.combinedModel ?? ""}</span>
          <ClickToCopy text={info.row.original.vehicle?.combinedVIN} textClassName="text-xs" />
          <span className="text-xs">{info.row.original.vehicle?.odometerInMiles ? `${info.row.original.vehicle.odometerInMiles.toLocaleString()} miles` : "No Mileage Found"}</span>
          <span className="text-xs">Created: {formatDateTime(info.row.original.created, DateTime.DATE_SHORT)}</span>
          <UnlistedKTypeUpdate part={info.row.original} />
        </div>,
      }),
      colHelper.accessor(row => {
        const offer = row.offers?.[0];
        const offerPrice = offer
          ? (offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice)
          : null;
        return offerPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: "Price",
        cell: (info) => <UnlistedOfferPrice part={info.row.original} />
      }),
      // colHelper.accessor("offers", {
      //   id: "error state",
      //   header: "Error",
      //   sortingFn: "auto",
      //   cell: (info) => <TableListingError part={info.row.original} />
      // }),
      colHelper.accessor(row => row.images.some(img => img.imageType === IPartImageType.Damage), {
        header: "Damage",
        cell: (info) => {
          const hasDamage = info.getValue();
          const damageImages = info.row.original.images.filter(img => img.imageType === IPartImageType.Damage);

          return (
            <span className="flex justify-center text-xl">
              {hasDamage ? (
                <PopOutBox
                  classes="max-w-3xl w-[40vw] max-h-[50vh] overflow-y-auto"
                  icon={<ExclamationTriangleIcon className="h-6 w-6 text-red-500" />}
                >
                  <div className="p-2">
                    <p className="text-sm font-medium mb-2">Damage Images:</p>
                    <div className="space-y-2">
                      {damageImages.map((image, index) => (
                        <img
                          key={index}
                          src={image.url}
                          alt={`Damage ${index + 1}`}
                          className="w-full h-auto rounded border"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      ))}
                    </div>
                  </div>
                </PopOutBox>
              ) : (
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              )}
            </span>
          );
        },
      }),
      colHelper.display({
        id: "mpn image",
        header: "MPN",
        cell: (info) => <UnlistedNpmUpdate part={info.row.original} />,
      }),
      colHelper.accessor("warehouseLocationId", {
        header: "Warehouse",
        sortingFn: "auto",
        cell: (info) => <WarehouseLocationFromId id={info.getValue()} lastStockChecked={info.row.original.lastStockChecked} />,
      }),
      colHelper.accessor("grade", {
        header: "Grade",
        cell: (info) => <UnlistedGradeUpdate part={info.row.original} />,
      }),
      // colHelper.accessor("vehicle", {
      //   id: "mileage",
      //   header: "Mileage",
      //   cell: (info) => <span className="flex justify-center">{info.getValue()?.odometerInMiles?.toLocaleString() ?? "N/A"}</span>,
      // }),
      // colHelper.accessor("created", {
      //   id: "created",
      //   header: "Created",
      //   cell: (info) => <span className="flex justify-center">{formatDateTime(info.getValue(), DateTime.DATE_SHORT)}</span>,
      // }),
      // colHelper.accessor("offers", {
      //   id: "offer status",
      //   header: "Offer Status",
      //   enableSorting: false,
      //   cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original} />,
      // }),
      colHelper.accessor("partStatus", {
        header: "Part Status",
        enableSorting: false,
        cell: (info) => <PartStatusLabel part={info.row.original} />,
      }),
      colHelper.display({
        header: "Ready To List",
        enableSorting: false,
        cell: (info) => <div className="flex flex-col items-center gap-y-2">
          {info.row.original.batchItem && (
            <RecentlyListedErrorCell batchItem={info.row.original.batchItem} showExtraInfo={false} />
          )}
          <UnlistedReadyStatus part={info.row.original} />
        </div>,
      }),
    ],
    [allCategories.isLoading, allCategories.data]
  );



  const bulkList = (rows: RowModel<IPart>, table: Table<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    tableRef.current = table;
    setSelectedPartsForBulkList(rows.rows.map((row) => row.original));
    setBulkListModalOpen(true);
  };

  const handleBulkListComplete = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
      tableRef.current = null;
    }
  };

  const handleAutoReorderImages = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const ids = selected.map((row) => row.original.id);

    autoReorderImages.mutate(ids, bulkSaveHandlers);
  }

  const handleApplyBulkDefaults = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.id)
    };

    useBulkDefaultPartCategory.mutate(body as any, {
      ...bulkSaveHandlers,
      onSuccess: () => {
        bulkSaveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
      }
    });
  };


  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate({ partIds }, bulkSaveHandlers);
  };


  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const expandedRow = (part: IPart) => {
    return <UnlistedExpandedRowContent part={part} />;
  };

  const extraRow = (part: IPart) => {
    return <div className="p-1 py-2">
      <EbayListingTitle part={part} onlyTitle={true} />
    </div>;
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  useEffect(() => {
    setAdditionalBody({
      categoryId: watchPartCategory?.id,
      listingStatus: watchListingStatus,
      locationSearchMode: watchLocationSearchMode,
      warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      dateFieldType: dateFieldType,
    });

  }, [watchPartCategory, watchListingStatus, watchWarehouseLocation, watchLocationSearchMode, watchWarehouseLocationBeginsWith, dates, dateFieldType]);

  // Monitor PagedTable query state to prevent crashes
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    // Subscribe to query cache changes and find any PagedTable queries
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter(query =>
        query.queryKey[0] === "pagedTable"
      );

      // Get the most recent PagedTable query state
      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching || fetchLoading,
    [queryState, isSearching, fetchLoading]
  );


  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
          onComplete={handleBulkListComplete}
        />
      )}

      <form className="flex flex-wrap gap-x-6 gap-y-2 items-center">
        <div className="flex">
          <ComboBoxInput
            label={"Part Category"}
            register={register}
            registerName={"partCategory"}
            options={allCategories.data?.data ?? []}
            setValue={setValue}
            disabled={isSearchDisabled || allCategories.isLoading}
            searchField={"name"}
            classes="min-w-96"
            placeholder="Start Typing to Search For a Category"
          />
        </div>

        <WarehouseLocationSearch
          showSearchOptions={true}
          register={register}
          setValue={setValue}
          watch={watch}
          isSearchDisabled={isSearchDisabled}
          warehouseLocations={warehouseLocations}
        />

        {/* Date Range Filter */}
        <DateRangeFilter
          dates={dates}
          onDatesChange={handleDatesChange}
          dateFieldType={dateFieldType}
          onDateFieldTypeChange={setDateFieldType}
        />

      </form>


      <PagedTable
        url="/part/listings"
        pageMode={PageMode.Unlisted}
        columns={cols}
        queryLabel="Search part details"
        // customUrlQuery={`pageSize=${pageSize}`}
        customUrlQuery=""
        // onRowClick={(x: IPart) => openInNewTab(`ebay-listings/${x.id}`)}
        actionButtons={[
          { label: "Bulk List", onClick: bulkList, isLoading: bulkListLoading, disabledOnNoRows: true },
          { label: "Auto Reorder Images", onClick: handleAutoReorderImages, isLoading: autoReorderImages.isLoading, disabledOnNoRows: true },
          { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
          { label: "Reset Description", onClick: handleBulkResetDescription, isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
          { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },
        ]}
        expandedRow={expandedRow}
        extraRow={extraRow}
        showTotal
        additionalBody={additionalBody}
        modeToggles={[
          { label: "Missing/Deleted", pageMode: PageMode.UnlistedMissingDeleted },
          { label: "QC", pageMode: PageMode.UnlistedQC },
          { label: "Dismantled", pageMode: PageMode.UnlistedDismantled },
          { label: "Not Located", pageMode: PageMode.UnlistedNotLocated },
          { label: "No Images", pageMode: PageMode.UnlistedNoImages },
          { label: "No Price", pageMode: PageMode.UnlistedNoPrice },
          { label: "No MPN", pageMode: PageMode.UnlistedNoMpn },
          { label: "Has Price", pageMode: PageMode.UnlistedHasPrice },
          { label: "Has MPN", pageMode: PageMode.UnlistedHasMpn },
          { label: "Has OE MPN", pageMode: PageMode.UnlistedHasOeMpn },
        ]}
      />
      {/* <div className="flex gap-x-2 items-center mt-4">
        <label>Page Size:</label>
        <input className="w-20" type="number" value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value))} />
      </div> */}
    </>
  );
}
